// chat-floating.component.ts
import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Input, HostBinding } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { setting } from '../../../../setting';

type AttachmentKind = 'image' | 'video' | 'pdf';

interface ChatAttachment {
  name: string;
  kind: AttachmentKind;
  size: number;
  // Solo existe en la sesión actual (blob URL); no se persiste en localStorage
  url?: SafeUrl;
}

interface PendingAttachment extends ChatAttachment {
  file: File;
  rawUrl: string;
}

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 20;

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isLoading?: boolean;
  attachments?: ChatAttachment[];
  app_id: number;
  client_id: number;
  user_application_id: number
}

@Component({
  selector: 'app-chat-floating',
  templateUrl: './chat-floating.component.html',
  styleUrls: ['./chat-floating.component.scss']
})
export class ChatFloatingComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() user: any;
  @Input() appId: number = -1;

  isOpen = false;
  newMessage = '';
  isLoading = false;
  messages: ChatMessage[] = [];
  now = new Date();
  avatarUrl = 'assets/img/app/helpdesk/sofi.png';
  avatarError = false;
  pendingAttachments: PendingAttachment[] = [];
  attachError = '';
  private objectUrls: string[] = [];
  private attachErrorTimeout: any;
  private clockInterval: any;
  getClientsSettingAppAuth: any;
  // private apiUrl = setting.fusionPaxTest; // Cambiar por la URL de tu API
private apiUrl = '';
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    // private notificationService: NotificationService
  ) {}

  // Expone el color como variable CSS para usar var(--client-color) en el scss
  @HostBinding('style.--client-color')
  get clientColor(): string {
    return this.getClientsSettingAppAuth?.clients_setting_global?.color_main_1 || 'black';
  }

  ngOnInit(): void {
    this.getClientsSettingAppAuth = JSON.parse(localStorage.getItem(setting.name));
    this.clockInterval = setInterval(() => this.now = new Date(), 30000);
    // Cargar historial desde localStorage
    const saved = localStorage.getItem('chat_history');
    if (saved) {
      try {
        this.messages = JSON.parse(saved);
      } catch {
        this.messages = [];
      }
    }
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  openFilePicker(): void {
    if (this.isLoading) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = ''; // permite volver a elegir el mismo archivo

    for (const file of files) {
      if (this.pendingAttachments.length >= MAX_ATTACHMENTS) {
        this.showAttachError(`Máximo ${MAX_ATTACHMENTS} archivos por mensaje`);
        break;
      }
      const kind = this.getAttachmentKind(file);
      if (!kind) {
        this.showAttachError('Solo se permiten imágenes, videos y PDF');
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        this.showAttachError(`"${file.name}" supera los ${MAX_FILE_SIZE_MB} MB`);
        continue;
      }
      const rawUrl = URL.createObjectURL(file);
      this.objectUrls.push(rawUrl);
      this.pendingAttachments.push({
        file,
        rawUrl,
        name: file.name,
        kind,
        size: file.size,
        url: this.sanitizer.bypassSecurityTrustUrl(rawUrl),
      });
    }
  }

  removeAttachment(index: number): void {
    const [removed] = this.pendingAttachments.splice(index, 1);
    if (removed) {
      URL.revokeObjectURL(removed.rawUrl);
      this.objectUrls = this.objectUrls.filter(u => u !== removed.rawUrl);
    }
  }

  formatSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  sendMessage(): void {
    const text = this.newMessage.trim();
    const files = this.pendingAttachments;
    if ((!text && !files.length) || this.isLoading) {
      return;
    }

    // Mensaje del usuario
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      text: text,
      sender: 'user',
      timestamp: new Date(),
      attachments: files.map(({ name, kind, size, url }) => ({ name, kind, size, url })),
      app_id: this.appId,
      client_id: this.user?.clientFx.id,
      user_application_id: this.user?.id,
    };
    this.messages.push(userMsg);
    this.newMessage = '';
    this.pendingAttachments = [];
    this.saveMessages();
    this.scrollToBottom();

    // Indicador de carga
    const loadingMsg: ChatMessage = {
      id: 'loading',
      app_id: this.appId,
      client_id: this.user?.clientFx.id,
      user_application_id: this.user?.id,
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isLoading: true
    };
    this.messages.push(loadingMsg);
    this.isLoading = true;

    const fields = {
      app_id: this.appId,
      client_id: this.user?.clientFx.id,
      user_application_id: this.user?.id.toString(),
      message: text
    }

    // Con adjuntos se envía multipart/form-data; sin adjuntos, JSON como antes
    let payload: any = fields;
    if (files.length) {
      payload = new FormData();
      Object.entries(fields).forEach(([key, value]) => payload.append(key, String(value ?? '')));
      files.forEach(a => payload.append('files', a.file, a.name));
    }

    // Llamar a la API
    this.http.post<any>(this.apiUrl, payload)
    .subscribe({
      next: (response) => {
        this.messages = this.messages.filter(m => m.id !== 'loading');
        this.messages.push({
          id: Date.now().toString(),
          text: response.data?.reply?.content || 'Gracias por tu mensaje. ¿En qué más puedo ayudarte?',
          sender: 'bot',
          timestamp: new Date(),
          app_id: this.appId,
          client_id: this.user?.clientFx.id,
          user_application_id: this.user?.id,
        });
        this.isLoading = false;
        this.saveMessages();
        this.scrollToBottom();
      },
      error: (error) => {
        console.error('Error en chat:', error);
        this.messages = this.messages.filter(m => m.id !== 'loading');
        this.messages.push({
          id: Date.now().toString(),
          text: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
          sender: 'bot',
          timestamp: new Date(),
          app_id: this.appId,
          client_id: this.user?.clientFx.id,
          user_application_id: this.user?.id,
        });
        this.isLoading = false;
        this.saveMessages();
        this.scrollToBottom();
        // this.notificationService.error('Error al conectar con el asistente');
      }
    });
  }

  private getAttachmentKind(file: File): AttachmentKind | null {
    if (file.type.startsWith('image/')) {
      return 'image';
    }
    if (file.type.startsWith('video/')) {
      return 'video';
    }
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    return null;
  }

  private showAttachError(message: string): void {
    this.attachError = message;
    clearTimeout(this.attachErrorTimeout);
    this.attachErrorTimeout = setTimeout(() => this.attachError = '', 4000);
  }

  private getChatContext(): string {
    // Enviar historial reciente para contexto
    return this.messages
      .filter(m => m.id !== 'loading')
      .slice(-10)
      .map(m => `${m.sender}: ${m.text}`)
      .join('\n');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 50);
  }

  private saveMessages(): void {
    // Guardar solo los últimos 50 mensajes (sin las URLs de adjuntos, que no sobreviven a un reload)
    const toSave = this.messages.slice(-50).map(m => m.attachments
      ? { ...m, attachments: m.attachments.map(({ name, kind, size }) => ({ name, kind, size })) }
      : m);
    localStorage.setItem('chat_history', JSON.stringify(toSave));
  }

  ngOnDestroy(): void {
    clearInterval(this.clockInterval);
    clearTimeout(this.attachErrorTimeout);
    this.objectUrls.forEach(u => URL.revokeObjectURL(u));
  }

  sendQuickSuggestion(text: string): void {
  this.newMessage = text;
  this.sendMessage();
}
}
